import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { getUnreadCount, getUnreadMessageCount } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  LayoutDashboard, FileText, Settings, Bell, LogOut, Menu, X, ClipboardList, HelpCircle, MessageSquare, Tag 
} from 'lucide-react';

// Helper function to check if user can access admin features
const canAccessAdminFeatures = (user) => {
  if (!user || !user.roles) return false;
  const roles = user.roles;
  return roles.includes('ADMIN') || roles.includes('OPS_COORDINATOR');
};

// Helper function to check if user can manage categories (Admin, Ops, or Manager)
const canManageCategories = (user) => {
  if (!user || !user.roles) return false;
  const roles = user.roles;
  return roles.includes('ADMIN') || roles.includes('OPS_COORDINATOR') || roles.includes('MANAGER');
};

export default function Layout({ children }) {
  const { user, logout, isAdminOrOps, hasRole } = useAuth();
  const { locations, selectedLocation, setSelectedLocation, getAccessibleLocations } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    try {
      const [notifRes, msgRes] = await Promise.all([
        getUnreadCount(),
        getUnreadMessageCount()
      ]);
      setUnreadCount(notifRes.data.count);
      setUnreadMessageCount(msgRes.data.count);
    } catch (error) {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000); // Poll every 30s
      return () => clearInterval(interval);
    }
  }, [user, loadUnreadCount]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Use both context function AND direct role check for maximum reliability
  const isAdminOrOpsUser = canAccessAdminFeatures(user) || isAdminOrOps();
  const canManageCats = canManageCategories(user);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/messages', label: 'Messages', icon: MessageSquare, badge: unreadMessageCount },
    { path: '/notifications', label: 'Notifications', icon: Bell, badge: unreadCount },
    // Categories for Admin/Ops/Manager (but NOT if they have full Settings access - avoid duplication)
    ...(!isAdminOrOpsUser && canManageCats ? [{ path: '/categories', label: 'Categories', icon: Tag }] : []),
    // Audit Log for Admin/Ops
    ...(isAdminOrOpsUser ? [{ path: '/audit-log', label: 'Audit Log', icon: ClipboardList }] : []),
    // Settings for Admin/Ops
    ...(isAdminOrOpsUser ? [{ path: '/settings', label: 'Settings', icon: Settings }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  // Determine if user can change location
  const canChangeLocation = isAdminOrOpsUser;
  const accessibleLocations = getAccessibleLocations();

  // Get role badges
  const getRoleBadges = () => {
    if (!user?.roles) return null;
    return user.roles.map(role => (
      <Badge key={role} className="bg-purple-100 text-purple-800 text-xs ml-1">
        {role.replace('_', ' ')}
      </Badge>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_assettracker-35/artifacts/1iv310t5_logo%20jf.png"
                alt="STPAssets"
                className="h-10"
              />
              <span className="font-bold text-xl text-purple-700 hidden sm:block" style={{ fontFamily: 'Manrope, sans-serif' }}>
                STPAssets
              </span>
            </div>

            {/* Location Selector */}
            <div className="flex-1 max-w-xs mx-4">
              <Select
                value={selectedLocation || ''}
                onValueChange={setSelectedLocation}
                disabled={!canChangeLocation && accessibleLocations.length <= 1}
              >
                <SelectTrigger className="h-10" data-testid="location-selector">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {accessibleLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-2">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActive(item.path) ? 'default' : 'ghost'}
                    className={`relative ${isActive(item.path) ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                    {item.badge > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-red-500 text-white text-xs p-0">
                        {item.badge}
                      </Badge>
                    )}
                  </Button>
                </Link>
              ))}
              <a href="/user-guide.html" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" data-testid="help-btn">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Help
                </Button>
              </a>
              <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div
                    className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                      isActive(item.path) ? 'bg-purple-100 text-purple-700' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </div>
                    {item.badge > 0 && (
                      <Badge className="bg-red-500 text-white">{item.badge}</Badge>
                    )}
                  </div>
                </Link>
              ))}
              <a
                href="/user-guide.html"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2 text-gray-700"
              >
                <HelpCircle className="h-5 w-5" />
                User Guide
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2 w-full text-left text-gray-700"
              >
                <LogOut className="h-5 w-5" />
                Logout
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* User Info Bar */}
      <div className="bg-purple-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-purple-700">
              Welcome, <span className="font-medium">{user?.name}</span>
            </span>
            <div className="flex items-center">
              {getRoleBadges()}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t px-2 py-2 z-40">
        <div className="flex items-center justify-around">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className="flex-1">
              <div
                className={`flex flex-col items-center py-1 ${
                  isActive(item.path) ? 'text-purple-600' : 'text-gray-500'
                }`}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs mt-1">{item.label.length > 8 ? item.label.substring(0, 7) + '…' : item.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </nav>

      {/* Bottom padding for mobile nav */}
      <div className="md:hidden h-16" />
    </div>
  );
}
